import * as Sentry from '@sentry/browser';
import { ActionKeys } from '~/actions/constants';
import { ActionTypes } from '~/actions/index';
import { cloneDeep } from 'lodash';
import omit from 'lodash-es/omit';
import * as moment from 'moment';
import BusinessRule from '~/types/BusinessRule';
import { BatchEntryExtended } from '~/types/OrderBatchEntry';
import { listToMapById } from '~/utils/list';

export type BatchEntryState = {
  data: HashMap<number, BatchEntryExtended>;
  // TODO: This only supports a single batch entry per order, so no return batch entries
  byOrderId: HashMap<string, number[]>;
  loadState: HashMap<string, boolean>;
  associatedRules: HashMap<number, BusinessRule[]>;
};

const initialState: BatchEntryState = {
  data: {},
  byOrderId: {},
  loadState: {},
  associatedRules: {},
};

// This lets us isolate some complex behavior.
// We only want to add missing content OR replace elements that have the same fulfillment rank.
function buildNextByOrderIdFromPayload(
  payloadBatchEntries: BatchEntryExtended[],
  data: HashMap<number, BatchEntryExtended>,
  _oldByOrderId: Record<string, number[]>,
) {
  const clonedByOrderId = cloneDeep(_oldByOrderId);

  payloadBatchEntries.forEach((payloadBatchEntry) => {
    const finalOrderBatchEntryIds: number[] = [];

    const batchEntryIds = clonedByOrderId[payloadBatchEntry.orderObjectId];
    if (batchEntryIds?.length) {
      batchEntryIds.forEach((batchEntryId) => {
        const batchEntryData = data[batchEntryId];

        const shouldKeepThisBatchEntryId =
          !!batchEntryData &&
          batchEntryData.partialFulfillmentRank !== payloadBatchEntry.partialFulfillmentRank &&
          batchEntryId !== payloadBatchEntry.id;

        if (shouldKeepThisBatchEntryId) {
          finalOrderBatchEntryIds.push(batchEntryId);
        }
      });
    }

    finalOrderBatchEntryIds.push(payloadBatchEntry.id);
    clonedByOrderId[payloadBatchEntry.orderObjectId] = finalOrderBatchEntryIds;
  });

  return clonedByOrderId;
}

function reducer(state: BatchEntryState = initialState, action: ActionTypes): BatchEntryState {
  switch (action.type) {
    case ActionKeys.ORDER_BATCH_ENTRY_FETCH:
      // If the batch entry was already loading return existing state
      if (state.loadState[action.payload.orderObjectId]) return state;

      return {
        ...state,
        loadState: {
          ...state.loadState,
          [action.payload.orderObjectId]: true,
        },
      };

    case ActionKeys.ORDER_CREATE_PARTIAL_FULFILLMENT_REQUESTS_SUCCESS: {
      const { order } = action.payload;
      const { partialFulfillmentRequests } = order;
      const data = { ...state.data };
      const byOrderId = {
        ...state.byOrderId,
      };

      // reset order batch entries (deleted in backend)
      byOrderId[order.objectId] = [];

      partialFulfillmentRequests.forEach((batchEntry) => {
        data[batchEntry.id] = batchEntry;
        byOrderId[order.objectId].push(batchEntry.id);
      });

      return {
        ...state,
        data,
        byOrderId,
      };
    }
    case ActionKeys.ORDER_BATCH_ENTRY_FETCH_ERROR:
      // TODO: Save error
      return {
        ...state,
        loadState: {
          ...state.loadState,
          [action.payload.orderObjectId]: false,
        },
      };

    case ActionKeys.ORDER_BATCH_ENTRIES_FETCH:
      return {
        ...state,
        loadState: {
          ...state.loadState,
          ...action.payload.orderObjectIds.reduce(
            (obj, orderObjectId) => ({ ...obj, [orderObjectId]: true }),
            {},
          ),
        },
      };
    case ActionKeys.ORDERS_PATCH_UPDATE_BATCH_ENTRIES_EXTRAS_SUCCESS:
    case ActionKeys.ORDERS_UPDATE_BATCH_ENTRIES_EXTRAS_SUCCESS:
    case ActionKeys.ORDERS_UPDATE_BATCH_ENTRIES_SERVICE_LEVEL_TOKEN_SUCCESS:
    case ActionKeys.ORDERS_PATCH_BATCH_ENTRIES_SUCCESS:
    case ActionKeys.ORDERS_UPDATE_PARCEL_SUCCESS:
    case ActionKeys.ORDER_BATCH_ENTRIES_FETCH_SUCCESS:
      const nextState: BatchEntryState = {
        ...state,
        data: {
          ...state.data,
          ...action.payload.batchEntries.reduce<Record<string, BatchEntryExtended>>(
            (accumulator, batchEntry) => {
              accumulator[batchEntry.id] = batchEntry;
              return accumulator;
            },
            {},
          ),
        },
        loadState: {
          ...state.loadState,
          ...action.payload.batchEntries.reduce<Record<string, boolean>>(
            (accumulator, batchEntry) => {
              accumulator[batchEntry.orderObjectId] = false;
              return accumulator;
            },
            {},
          ),
        },
      };

      const nextByOrderId = buildNextByOrderIdFromPayload(
        action.payload.batchEntries,
        nextState.data,
        nextState.byOrderId,
      );
      nextState.byOrderId = nextByOrderId;

      // Don't return the new state unless it changed. This avoids extra renders in the UI.
      return JSON.stringify(nextState) === JSON.stringify(state) ? state : nextState;
    case ActionKeys.ORDER_BATCH_ENTRIES_FETCH_ERROR:
      return {
        ...state,
        loadState: {
          ...state.loadState,
          ...action.payload.orderObjectIds.reduce(
            (obj, orderObjectId) => ({ ...obj, [orderObjectId]: false }),
            {},
          ),
        },
      };
    case ActionKeys.BATCH_ENTRY_RULES_FETCH_SUCCESS:
      return {
        ...state,
        associatedRules: {
          ...state.associatedRules,
          [action.payload.batchEntryId]: [...action.payload.rules],
        },
      };
    case ActionKeys.BATCH_ENTRY_RULES_CLEAR:
      return {
        ...state,
        associatedRules: omit(state.associatedRules, action.payload.batchEntryId),
      };
    // This is so splits render right away
    case ActionKeys.ORDERS_FETCH_SUCCESS:
      try {
        return {
          ...state,
          data: {
            ...state.data,
            ...action.payload.orders.reduce(
              (obj, order) => ({
                ...obj,
                ...order.partialFulfillmentRequests.reduce(
                  (obj, batchEntry) => ({ ...obj, [batchEntry.id]: batchEntry }),
                  {},
                ),
              }),
              {},
            ),
          },
          byOrderId: {
            ...state.byOrderId,
            ...action.payload.orders.reduce(
              (obj, order) => ({
                ...obj,
                [order.objectId]: order.partialFulfillmentRequests.map(
                  (batchEntry) => batchEntry.id,
                ),
              }),
              {},
            ),
          },
        };
      } catch (err) {
        Sentry.captureException(err);
        //No-op
        return state;
      }
    // This clears batch entries, mostly used so re-purchase doesn't show as a split
    case ActionKeys.ORDERS_CLEAR_BATCH_ENTRIES: {
      if (action.payload.resetBatchEntries.length === 0) return state;

      const newRankMap = action.payload.resetBatchEntries.reduce<
        Record<string, Record<number, number>>
      >((obj, batchEntry) => {
        return {
          ...obj,
          [batchEntry.orderObjectId]: {
            ...obj[batchEntry.orderObjectId],
            [batchEntry.partialFulfillmentRank]: batchEntry.id,
          },
        };
      }, {});

      const oldBeMap = listToMapById(action.payload.initialBatchEntries);

      const { ids, orderIds } = action.payload.initialBatchEntries.reduce(
        (obj: { ids: number[]; orderIds: string[] }, be) => ({
          ids: [...obj.ids, be.id],
          orderIds: [...obj.orderIds, be.orderObjectId],
        }),
        { ids: [], orderIds: [] },
      );

      return {
        ...state,
        data: { ...omit(state.data, ids), ...listToMapById(action.payload.resetBatchEntries) },
        byOrderId: {
          ...state.byOrderId,
          ...orderIds.reduce((obj, curr) => {
            return {
              ...obj,
              [curr]: state.byOrderId[curr].map((beId) => {
                const oldBe = oldBeMap[beId] as BatchEntryExtended;
                // Guard in case this is misused.
                // In theory the batch entries already selected this
                // will always be in the initalBatchEntries list.
                if (!oldBe) return beId;
                const oldRank = oldBe.partialFulfillmentRank;
                const newBeId = newRankMap[curr]?.[oldRank];

                return newBeId || beId;
              }),
              // [curr]: difference(state.byOrderId[curr], ids),
            };
          }, {}),
        },
        loadState: {
          ...state.loadState,
          ...orderIds.reduce((obj, curr) => {
            return {
              ...obj,
              [curr]: true,
            };
          }, {}),
        },
      };
    }

    case ActionKeys.ORDERS_UPDATE_BATCH_ENTRIES_SHIPMENT_DATE_INITIATED: {
      const { batchEntryIds, shipmentDate } = action.payload;

      const data = { ...state.data };
      let shipDate = shipmentDate;
      if (!moment.isMoment(shipmentDate)) {
        shipDate = moment(shipDate);
      }
      batchEntryIds.forEach((batchEntryId) => {
        data[batchEntryId] = {
          ...data[batchEntryId],
          shipmentDate: shipDate as moment.Moment,
        };
      });

      return {
        ...state,
        data,
      };
    }

    default:
      return state;
  }
}

export default reducer;
